import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

export type FilterType = 'multiple' | 'single' | 'date';

export interface FilterConfig {
  label: string;
  options: FilterOption[];
  type: FilterType;        
}

interface FilterOption{
  key: string;
  value: string;
}

export interface SelectedFilters {
  [key: string]: string | string[];
}

export interface FilterAction{
  action: 'APPLY' | 'CANCEL';
  resp: SelectedFilters | null;
}

@Component({
  selector: 'app-listing-filters',
  standalone: false,
  templateUrl: './listing-filters.component.html',
  styleUrl: './listing-filters.component.scss'
})
export class ListingFiltersComponent {

  @Input() filterConfig: FilterConfig[] = [];
  @Input() selectedFilters: SelectedFilters = {};
  @Output() filterAction = new EventEmitter<FilterAction>();
  
  isPannelVisible:boolean = false;
  
  selected: SelectedFilters = {};
  filterForm!: FormGroup;
  initialFilterValues:any;

  searchControls: { [label: string]: FormControl } = {};

  constructor(private fb: FormBuilder){
    this.filterForm = this.fb.group({});
  }

  ngOnChanges() {
    this.selected = { ...this.selectedFilters };

    this.filterConfig.forEach(filter => {
      this.searchControls[filter.label] = new FormControl('');
      const controlValue = filter.type === 'multiple' ? [] : null;
      this.filterForm.addControl(filter.label, this.fb.control(controlValue));
    });

    this.initialFilterValues = {...this.filterForm.value};
  }

  togglePannelVisibility(){
    this.isPannelVisible = !this.isPannelVisible;
    this.resetToInitialFilterValues();
  }

  resetToInitialFilterValues(){
    this.filterForm.patchValue(this.initialFilterValues);
  }

  apply() {
    let formResp = this.filterForm.value;
    for(let label in formResp){
      if(formResp[label].length > 0){
        formResp[label] = formResp[label].filter( (value:any) => (value != undefined ||value != null));
      }
    }
    this.filterAction.emit({
      action: 'APPLY',
      resp: formResp
    });
    this.isPannelVisible = false;
  }

  cancel(){
    this.isPannelVisible = false;
    this.resetToInitialFilterValues();
  }

  getFilteredOptions(filter: any): any[] {
    const searchTerm = this.searchControls[filter.label].value?.toLowerCase() || '';
    return filter.options.filter((opt: any) =>
      opt.key.toLowerCase().includes(searchTerm)
    );
  }
  
  toggleSelectAll(filter: any, event: Event): void {
    event.stopPropagation();
  
    const control = this.filterForm.get(filter.label);
    const options = this.getFilteredOptions(filter);
    const allValues = options.map(o => o.value);
  
    const selected = control?.value || [];
  
    const hasAll = allValues.every(v => selected.includes(v));
    const newSelection = hasAll
      ? selected.filter((v: any) => !allValues.includes(v)) // deselect all visible
      : [...new Set([...selected, ...allValues])];          // select all visible
  
    control?.setValue(newSelection);
  }
  
  isAllSelected(filter: any): boolean {
    const control = this.filterForm.get(filter.label);
    const selected = control?.value || [];
    const visibleOptions = this.getFilteredOptions(filter);
    return visibleOptions.every(o => selected.includes(o.value)) && visibleOptions.length > 0;
  }
  
  isIndeterminate(filter: any): boolean {
    const control = this.filterForm.get(filter.label);
    const selected = control?.value || [];
    const visibleOptions = this.getFilteredOptions(filter);
  
    const selectedVisible = visibleOptions.filter(o => selected.includes(o.value));
    return selectedVisible.length > 0 && selectedVisible.length < visibleOptions.length;
  }
  
}
